import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { createChart, IChartApi, ISeriesApi, CandlestickData } from 'lightweight-charts';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  RefreshCw,
  Wallet,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import useStore from '../store/useStore';
import {
  ServiceInfo,
  getDemoPrediction,
  getPaymentRequirements,
  getNonce,
  createPaymentPayload,
  getPaidPrediction,
  PredictionResult,
} from '../services/api';

interface PredictionPanelProps {
  serviceInfo: ServiceInfo | null;
  onBack: () => void;
}

const SYMBOLS = [
  { value: 'BTC', label: 'Bitcoin', icon: '₿' },
  { value: 'ETH', label: 'Ethereum', icon: 'Ξ' },
  { value: 'SOL', label: 'Solana', icon: '◎' },
  { value: 'AVAX', label: 'Avalanche', icon: '🔺' },
  { value: 'MATIC', label: 'Polygon', icon: '⬡' },
  { value: 'MON', label: 'Monad', icon: '⚡' },
];

const INTERVALS = [
  { value: '5m', label: '5分钟' },
  { value: '15m', label: '15分钟' },
  { value: '1h', label: '1小时' },
  { value: '4h', label: '4小时' },
  { value: '1d', label: '1天' },
];

export default function PredictionPanel({ onBack }: PredictionPanelProps) {
  const { wallet, prediction, payment, setPredictions, setLoading, setError, setPaymentStatus, setPaymentError, resetPayment, updateBalance } = useStore();
  
  const [selectedSymbol, setSelectedSymbol] = useState('BTC');
  const [selectedInterval, setSelectedInterval] = useState('1h');
  const [predictionLength, setPredictionLength] = useState(24);
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [isDemo, setIsDemo] = useState(false);
  
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);

  // 初始化图表
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { color: 'transparent' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: 'rgba(139, 92, 246, 0.1)' },
        horzLines: { color: 'rgba(139, 92, 246, 0.1)' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 400,
      crosshair: {
        vertLine: {
          color: '#8b5cf6',
          labelBackgroundColor: '#8b5cf6',
        },
        horzLine: {
          color: '#8b5cf6',
          labelBackgroundColor: '#8b5cf6',
        },
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    // 响应式调整
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (!candleSeriesRef.current || !result) return;

    const chartData: CandlestickData[] = result.predictions.map(p => ({
      time: (p.timestamp / 1000) as any,
      open: p.open,
      high: p.high,
      low: p.low,
      close: p.close,
    }));

    candleSeriesRef.current.setData(chartData);
    chartRef.current?.timeScale().fitContent();
  }, [result]);

  // 获取演示预测
  const handleDemoPredict = async () => {
    setLoading(true);
    setError(null);
    setIsDemo(true);

    try {
      const response = await getDemoPrediction(selectedSymbol, selectedInterval);
      setResult(response.prediction);
      setPredictions(response.prediction.predictions);
    } catch (err: any) {
      setError(err.message || '获取演示预测失败');
    } finally {
      setLoading(false);
    }
  };

  // 付费预测
  const handlePaidPredict = async () => {
    if (!wallet.isConnected || !wallet.signer) {
      setError('请先连接钱包');
      return;
    }

    setLoading(true);
    setError(null);
    setIsDemo(false);
    resetPayment();

    try {
      // 1. 获取支付要求
      setPaymentStatus('pending');
      const { paymentRequirements } = await getPaymentRequirements('prediction');
      const requirement = paymentRequirements.accepts[0];

      // 2. 获取 nonce
      const nonce = await getNonce(wallet.address!);

      // 3. 构造支付负载并签名
      const paymentHeader = await createPaymentPayload(
        wallet.signer,
        requirement,
        nonce
      );

      // 4. 执行实际转账（0.01 MON）
      setPaymentStatus('pending');
      const tx = await wallet.signer.sendTransaction({
        to: requirement.payTo,
        value: requirement.maxAmountRequired,
      });
      
      // 等待交易确认
      await tx.wait();
      
      // 5. 发送付费请求（带支付签名）
      const response = await getPaidPrediction(
        paymentHeader,
        selectedSymbol,
        selectedInterval,
        predictionLength
      );

      // 6. 更新余额
      await updateBalance();

      setPaymentStatus('success');
      setResult(response.prediction);
      setPredictions(response.prediction.predictions);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setPaymentError('支付验证失败');
      } else if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
        setPaymentError('用户取消了交易');
      } else if (err.code === 'INSUFFICIENT_FUNDS') {
        setPaymentError('余额不足，无法支付');
      } else {
        setError(err.message || '预测查询失败');
      }
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  // 计算价格变化
  const getPriceChange = () => {
    if (!result || result.predictions.length < 2) return null;
    const first = result.predictions[0].close;
    const last = result.predictions[result.predictions.length - 1].close;
    const change = ((last - first) / first) * 100;
    return change;
  };

  const priceChange = getPriceChange();

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* 顶部导航 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            返回首页
          </button>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左侧控制面板 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-6 lg:sticky lg:top-24 lg:self-start"
          >
            {/* 币种选择 */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">选择币种</h3>
              <div className="grid grid-cols-2 gap-3">
                {SYMBOLS.map(s => (
                  <button
                    key={s.value}
                    onClick={() => setSelectedSymbol(s.value)}
                    className={`p-3 rounded-xl border transition-all ${
                      selectedSymbol === s.value
                        ? 'bg-cyber-purple/20 border-cyber-purple text-white'
                        : 'bg-cyber-dark/50 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{s.icon}</span>
                    <span className="text-sm font-medium">{s.value}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 时间周期 */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">时间周期</h3>
              <div className="flex flex-wrap gap-2">
                {INTERVALS.map(i => (
                  <button
                    key={i.value}
                    onClick={() => setSelectedInterval(i.value)}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      selectedInterval === i.value
                        ? 'bg-cyber-blue text-white'
                        : 'bg-cyber-dark/50 text-gray-400 hover:text-white'
                    }`}
                  >
                    {i.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 预测长度 */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">预测长度</h3>
              <input
                type="range"
                min="6"
                max="120"
                value={predictionLength}
                onChange={e => setPredictionLength(parseInt(e.target.value))}
                className="w-full accent-cyber-purple"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>6</span>
                <span className="text-cyber-purple font-bold">{predictionLength} 个周期</span>
                <span>120</span>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="glass rounded-2xl p-6 space-y-3">
              <h3 className="text-lg font-bold text-white mb-4">开始预测</h3>
              
              <button
                onClick={handleDemoPredict}
                disabled={prediction.isLoading && isDemo}
                className="w-full py-3 rounded-xl border-2 border-cyber-purple/50 text-cyber-purple hover:bg-cyber-purple/10 hover:border-cyber-purple transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {prediction.isLoading && isDemo ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>预测中...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    <span>免费演示（6个预测点）</span>
                  </>
                )}
              </button>

              <button
                onClick={handlePaidPredict}
                disabled={prediction.isLoading || !wallet.isConnected || (prediction.isLoading && !isDemo)}
                className="btn-cyber w-full py-4 rounded-xl bg-gradient-to-r from-cyber-purple to-cyber-blue text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-cyber-purple/30 transition-all"
              >
                {prediction.isLoading && !isDemo ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>处理中...</span>
                  </>
                ) : (
                  <>
                    <Wallet className="w-5 h-5" />
                    <span>支付 0.01 MON 获取完整预测</span>
                  </>
                )}
              </button>

              {!wallet.isConnected && (
                <div className="p-3 rounded-lg bg-cyber-yellow/10 border border-cyber-yellow/30">
                  <p className="text-center text-sm text-cyber-yellow">
                    ⚠️ 请先连接钱包以使用付费功能
                  </p>
                </div>
              )}
            </div>

            {/* 支付状态 */}
            {payment.paymentStatus !== 'idle' && (
              <div className={`p-4 rounded-xl ${
                payment.paymentStatus === 'success' ? 'bg-cyber-green/10 border border-cyber-green/30' :
                payment.paymentStatus === 'failed' ? 'bg-cyber-red/10 border border-cyber-red/30' :
                'bg-cyber-purple/10 border border-cyber-purple/30'
              }`}>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {payment.paymentStatus === 'pending' && <Loader2 className="w-5 h-5 text-cyber-purple animate-spin" />}
                    {payment.paymentStatus === 'success' && <CheckCircle className="w-5 h-5 text-cyber-green" />}
                    {payment.paymentStatus === 'failed' && <AlertCircle className="w-5 h-5 text-cyber-red" />}
                    <span className={`text-sm font-medium ${
                      payment.paymentStatus === 'success' ? 'text-cyber-green' :
                      payment.paymentStatus === 'failed' ? 'text-cyber-red' :
                      'text-cyber-purple'
                    }`}>
                      {payment.paymentStatus === 'pending' && '正在处理支付...'}
                      {payment.paymentStatus === 'success' && '支付成功！余额已更新'}
                      {payment.paymentStatus === 'failed' && (payment.paymentError || '支付失败')}
                    </span>
                  </div>
                  {payment.paymentStatus === 'pending' && (
                    <p className="text-xs text-gray-400 ml-7">
                      请在弹出的钱包窗口中确认交易
                    </p>
                  )}
                  {payment.paymentStatus === 'success' && (
                    <p className="text-xs text-cyber-green/80 ml-7">
                      ✓ 0.01 MON 已从您的钱包扣除
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* 错误提示 */}
            {prediction.error && (
              <div className="p-4 rounded-xl bg-cyber-red/10 border border-cyber-red/30">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-cyber-red" />
                  <span className="text-sm text-cyber-red">{prediction.error}</span>
                </div>
              </div>
            )}
          </motion.div>

          {/* 右侧图表和结果 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* 标题卡片 */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyber-purple/20 to-cyber-blue/20 flex items-center justify-center text-2xl">
                    {SYMBOLS.find(s => s.value === selectedSymbol)?.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {selectedSymbol}/USDT
                    </h2>
                    <p className="text-gray-400">
                      {INTERVALS.find(i => i.value === selectedInterval)?.label} K线预测
                    </p>
                  </div>
                </div>

                {priceChange !== null && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                    priceChange > 0 ? 'bg-cyber-green/10 text-cyber-green' :
                    priceChange < 0 ? 'bg-cyber-red/10 text-cyber-red' :
                    'bg-cyber-yellow/10 text-cyber-yellow'
                  }`}>
                    {priceChange > 0 ? <TrendingUp className="w-5 h-5" /> :
                     priceChange < 0 ? <TrendingDown className="w-5 h-5" /> :
                     <Minus className="w-5 h-5" />}
                    <span className="font-bold">
                      {priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* K线图表 */}
            <div className="chart-container relative">
              <div ref={chartContainerRef} className="w-full" style={{ minHeight: '400px' }} />
              {!result && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-cyber-dark/90 backdrop-blur-sm rounded-lg">
                  <div className="text-center space-y-4 px-8">
                    <div className="w-16 h-16 rounded-full bg-cyber-purple/20 flex items-center justify-center mx-auto">
                      <TrendingUp className="w-8 h-8 text-cyber-purple" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-lg mb-2">准备开始预测</p>
                      <p className="text-gray-400 text-sm">
                        请在左侧选择参数，然后点击
                        <span className="text-cyber-purple font-bold mx-1">「免费演示」</span>
                        或
                        <span className="text-cyber-blue font-bold mx-1">「支付预测」</span>
                        按钮
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 预测结果表格 */}
            {result && (
              <div className="glass rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-cyber-purple/20">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-white">预测详情</h3>
                    {isDemo && (
                      <span className="px-3 py-1 rounded-full bg-cyber-yellow/20 text-cyber-yellow text-xs">
                        演示模式
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    模型: {result.modelInfo.name} | 置信度: {(result.confidence * 100).toFixed(1)}%
                  </p>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full data-table text-sm">
                    <thead className="sticky top-0">
                      <tr>
                        <th>时间</th>
                        <th>开盘</th>
                        <th>最高</th>
                        <th>最低</th>
                        <th>收盘</th>
                        <th>趋势</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.predictions.map((p, i) => (
                        <tr key={i}>
                          <td className="font-mono text-xs">
                            {new Date(p.timestamp).toLocaleString('zh-CN')}
                          </td>
                          <td className="font-mono">{p.open.toFixed(2)}</td>
                          <td className="font-mono text-cyber-green">{p.high.toFixed(2)}</td>
                          <td className="font-mono text-cyber-red">{p.low.toFixed(2)}</td>
                          <td className="font-mono font-bold">{p.close.toFixed(2)}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 ${
                              p.trend === 'up' ? 'text-cyber-green' :
                              p.trend === 'down' ? 'text-cyber-red' :
                              'text-cyber-yellow'
                            }`}>
                              {p.trend === 'up' && <TrendingUp className="w-4 h-4" />}
                              {p.trend === 'down' && <TrendingDown className="w-4 h-4" />}
                              {p.trend === 'neutral' && <Minus className="w-4 h-4" />}
                              {p.changePercent > 0 ? '+' : ''}{p.changePercent.toFixed(2)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

